import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Shield, Plus, Edit2, Trash2, Check,
    CheckCircle, XCircle, RefreshCw, AlertCircle, Loader2, Database
} from 'lucide-react';
import { dataQualityService } from '../../services/dataQualityService';
import CustomSelect from '../shared/CustomSelect';
import { useAuth } from '../../context/AuthContext';

const CompletenessRules = () => {
    const [rules, setRules] = useState([]);
    const [loading, setLoading] = useState(false);
    const [seeding, setSeeding] = useState(false);
    const [clientType, setClientType] = useState('RETAIL');
    const [message, setMessage] = useState({ text: '', type: '' });
    const navigate = useNavigate();
    const { hasPermission, user } = useAuth();

    const isSuperAdmin = user?.role === 'SUPER_ADMIN' || user?.roles?.includes('SUPER_ADMIN');

    useEffect(() => {
        fetchRules();
    }, [clientType]);

    const fetchRules = async () => {
        try {
            setLoading(true);
            const response = await dataQualityService.rules.getAll(clientType);
            const data = (response.success && response.data) ? response.data : (Array.isArray(response) ? response : []);
            setRules(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Failed to fetch rules:', error);
            showMessage('Failed to fetch completeness rules', 'error');
            setRules([]);
        } finally {
            setLoading(false);
        }
    };

    const showMessage = (text, type = 'success') => {
        setMessage({ text, type });
        setTimeout(() => setMessage({ text: '', type: '' }), 5000);
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this rule?')) {
            try {
                await dataQualityService.rules.delete(id);
                showMessage('Rule deleted successfully');
                fetchRules();
            } catch (error) {
                console.error('Failed to delete rule:', error);
                showMessage('Failed to delete rule', 'error');
            }
        }
    };

    const handleSeedDefaults = async () => {
        if (!window.confirm('This will create default completeness rules. Continue?')) return;
        try {
            setSeeding(true);
            await dataQualityService.rules.seedDefaults();
            showMessage('Default rules seeded successfully');
            fetchRules();
        } catch (error) {
            console.error('Failed to seed defaults:', error);
            showMessage('Failed to seed default rules', 'error');
        } finally {
            setSeeding(false);
        }
    };

    return (
        <div className="space-y-6">
            {message.text && (
                <div className={`p-4 rounded-lg flex items-center gap-2 ${
                    message.type === 'error'
                        ? 'bg-red-100 text-red-800 border border-red-200'
                        : 'bg-green-100 text-green-800 border border-green-200'
                }`}>
                    {message.type === 'error' ? <AlertCircle /> : <Check />}
                    {message.text}
                </div>
            )}

            <div className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-gray-100 border border-gray-200 rounded-lg flex items-center justify-center">
                            <Database className="w-6 h-6 text-gray-700" />
                        </div>
                        <div>
                            <h2 className="text-xl font-semibold text-gray-900">Completeness Rules</h2>
                            <p className="text-sm text-gray-500">Configure which fields are required for profile completeness scoring</p>
                        </div>
                    </div>
                    <button
                        onClick={fetchRules}
                        className="p-2 text-gray-600 hover:bg-gray-50 rounded-lg transition"
                    >
                        <RefreshCw className="w-5 h-5" />
                    </button>
                </div>
            </div>

            <div className="space-y-4">
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Client Type</label>
                                <CustomSelect
                                    value={clientType}
                                    onChange={setClientType}
                                    options={[
                                        { value: 'RETAIL', label: 'Retail' },
                                        { value: 'CORPORATE', label: 'Corporate' },
                                    ]}
                                    placeholder="Select type"
                                />
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            {isSuperAdmin && (
                                <button
                                    onClick={handleSeedDefaults}
                                    disabled={seeding}
                                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition flex items-center gap-2 disabled:opacity-50"
                                >
                                    {seeding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Database className="w-4 h-4" />}
                                    Seed Defaults
                                </button>
                            )}
                            <button
                                onClick={() => navigate('/data-quality/rules/new')}
                                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition flex items-center gap-2"
                            >
                                <Plus className="w-5 h-5" />
                                Add Rule
                            </button>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    {loading ? (
                        <div className="flex items-center justify-center h-64">
                            <RefreshCw className="w-8 h-8 text-gray-400 animate-spin" />
                        </div>
                    ) : rules.length === 0 ? (
                        <div className="text-center py-12">
                            <Database className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                            <p className="text-gray-500">No completeness rules found for {clientType}</p>
                            {isSuperAdmin && (
                                <button onClick={handleSeedDefaults} className="mt-4 text-blue-600 hover:text-blue-700 font-medium">
                                    Seed default rules
                                </button>
                            )}
                        </div>
                    ) : (
                        <table className="w-full">
                            <thead className="bg-gray-50 border-b border-gray-200">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Field</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Label</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client Type</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Required</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Weight</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Active</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 bg-white">
                                {rules.map((rule) => (
                                    <tr key={rule.id} className="hover:bg-gray-50 transition">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                            {rule.fieldName}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                                            {rule.fieldLabel}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {rule.clientType}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {rule.required ? (
                                                <span className="flex items-center gap-1 text-green-600">
                                                    <CheckCircle className="w-4 h-4" />
                                                    <span className="text-sm">Yes</span>
                                                </span>
                                            ) : (
                                                <span className="flex items-center gap-1 text-gray-400">
                                                    <XCircle className="w-4 h-4" />
                                                    <span className="text-sm">No</span>
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {rule.weight}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {rule.active ? (
                                                <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">Active</span>
                                            ) : (
                                                <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-600">Inactive</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() => navigate(`/data-quality/rules/${rule.id}/edit`)}
                                                    className="text-gray-600 hover:text-indigo-600 transition"
                                                    title="Edit"
                                                >
                                                    <Edit2 className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(rule.id)}
                                                    className="text-gray-600 hover:text-red-600 transition"
                                                    title="Delete"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CompletenessRules;
